import { Flex, Spacing, Tag, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ReviewEntryQuestion } from '../../types/reviewEntryQuestion'
import { excludeThirdPartyQuestions } from '../../utils/reviewEntryThirdPartyQuestion'
import { ReviewEntryQuestionHoverTrigger } from './elements/ReviewEntryQuestionHoverPopover'
import * as Styled from './styles'
import { getQuestionCounts } from './utils/getQuestionCounts'
import { getQuestionHoverContent } from './utils/getQuestionHoverContent'
import { getStatusTagConfig } from './utils/getStatusTagConfig'

interface ReviewEntriesStatusCellProps {
  questions: ReviewEntryQuestion[]
  status?: string
}

export const ReviewEntriesStatusCell = ({ questions, status }: ReviewEntriesStatusCellProps): ReactElement => {
  const { t } = useTranslation()
  const statusQuestions = useMemo(() => excludeThirdPartyQuestions(questions), [questions])
  const statusLabels = useMemo(
    () => ({
      processing: t('review_entries.status.processing'),
      toValidate: t('review_entries.status.to_validate'),
      validated: t('review_entries.status.validated'),
      written: t('review_entries.status.written'),
    }),
    [t],
  )
  const statusTag = useMemo(() => getStatusTagConfig(status, statusLabels), [status, statusLabels])

  const questionCounts = useMemo(() => getQuestionCounts({ questions: statusQuestions }), [statusQuestions])

  const questionHoverLabels = useMemo(
    () => ({
      fallbackMessage: t('review_entries.questions.hover.fallback_message'),
      levelHigh: t('review_entries.questions.hover.level.high'),
      levelLow: t('review_entries.questions.hover.level.low'),
      levelMedium: t('review_entries.questions.hover.level.medium'),
      suggestionFor: (type: string) => t('review_entries.questions.hover.suggestion_for', { type }),
    }),
    [t],
  )

  const questionHoverContent = useMemo(
    () => getQuestionHoverContent({ labels: questionHoverLabels, questions: statusQuestions }),
    [questionHoverLabels, statusQuestions],
  )

  const questionBadges = (
    <Flex alignItems="center" flexWrap="nowrap" gap={Spacing.S8}>
      {questionCounts.alert > 0 ? (
        <Tag color="red" icon="circleAlert" size="sm" type="subtle">
          {questionCounts.alert}
        </Tag>
      ) : null}
      {questionCounts.question > 0 ? (
        <Tag color="yellow" icon="circleQuestionMark" size="sm" type="subtle">
          {questionCounts.question}
        </Tag>
      ) : null}
    </Flex>
  )

  return (
    <Styled.ReviewEntriesStatusCellWrapper>
      <Styled.ReviewEntriesStatusTagWrapper>
        <Tag color={statusTag.color} icon={statusTag.icon} maxWidth="100%" size="sm" type="subtle">
          <Styled.ReviewEntriesStatusTagLabel>
            <Text as="span" inherit title={statusTag.label} truncate>
              {statusTag.label}
            </Text>
          </Styled.ReviewEntriesStatusTagLabel>
        </Tag>
      </Styled.ReviewEntriesStatusTagWrapper>
      {questionHoverContent ? (
        <ReviewEntryQuestionHoverTrigger content={questionHoverContent}>
          {questionBadges}
        </ReviewEntryQuestionHoverTrigger>
      ) : null}
    </Styled.ReviewEntriesStatusCellWrapper>
  )
}
